// Optimal String Alignment (OSA) edit distance, for the Garden vine search
// fuzzy fallback (Plan 02). OSA is the Damerau variant that scores an ADJACENT
// transposition as ONE edit while forbidding editing the same substring more
// than once. That single property is why search needs it: the two most common
// real typos are transpositions (`jhon`->`john`, `freind`->`friend`), and
// standard Levenshtein scores those as distance 2, so they would fail a
// distance-1 threshold.
//
// This is DELIBERATELY a separate algorithm from the two existing standard
// Levenshtein copies (`src/lib/topic-search.ts`, `src/lib/prayer-utils.ts`).
// Those implement and depend on standard distance (prayer-utils' copy is
// load-bearing for name-alias merge behavior, CLAUDE.md rule #30), so Plan 02
// does NOT consolidate or retrofit them. The pre-existing duplication of the
// two STANDARD Levenshteins is a separate rule #4 debt, banked independently;
// do NOT substitute this OSA variant for them.
//
// Zero-dep so probe scripts can import it under tsx with default resolution.
export function osaDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  // d[i][j] = OSA distance between a[0..i) and b[0..j).
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(
        d[i - 1][j] + 1, // deletion
        d[i][j - 1] + 1, // insertion
        d[i - 1][j - 1] + cost, // substitution
      );
      // Adjacent transposition (OSA): the two trailing chars are swapped.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, d[i - 2][j - 2] + 1);
      }
      d[i][j] = best;
    }
  }
  return d[m][n];
}

/**
 * Thresholded OSA distance for latency-sensitive fuzzy search.
 *
 * Only the diagonal band that can still finish within `maxDistance` is
 * evaluated, and only three rows are retained. Garden bounds both inputs
 * before calling this helper, so a pasted megatoken can never allocate the
 * quadratic matrix used by the general-purpose `osaDistance` API above.
 * Returns `maxDistance + 1` when the strings are outside the threshold.
 */
export function osaDistanceWithin(
  a: string,
  b: string,
  maxDistance: number,
): number {
  const limit = Math.max(0, Math.floor(maxDistance));
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > limit) return limit + 1;
  if (m === 0) return n <= limit ? n : limit + 1;
  if (n === 0) return m <= limit ? m : limit + 1;

  const outside = limit + 1;
  let previousPrevious = new Int16Array(n + 1);
  let previous = new Int16Array(n + 1);
  let current = new Int16Array(n + 1);
  previousPrevious.fill(outside);
  previous.fill(outside);
  for (let j = 0; j <= Math.min(n, limit); j += 1) previous[j] = j;

  for (let i = 1; i <= m; i += 1) {
    current.fill(outside);
    if (i <= limit) current[0] = i;
    const from = Math.max(1, i - limit);
    const through = Math.min(n, i + limit);
    for (let j = from; j <= through; j += 1) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      let best = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + cost,
      );
      if (
        i > 1 &&
        j > 1 &&
        a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        best = Math.min(best, previousPrevious[j - 2] + 1);
      }
      current[j] = Math.min(best, outside);
    }
    const recycled = previousPrevious;
    previousPrevious = previous;
    previous = current;
    current = recycled;
  }
  return previous[n] <= limit ? previous[n] : outside;
}

// Number of identical leading characters two strings share. A cheap
// match-quality signal for ranking fuzzy candidates that tie on edit distance:
// a misspelling's intended word usually shares a long prefix with the query
// (shepard/shepherd share "shep"), while a coincidental same-distance neighbor
// (a common short word like "heard") shares little or none. Zero-dep, same as
// osaDistance, so probe scripts import it under tsx with default resolution.
export function commonPrefixLength(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
  return i;
}
